import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import HerissonLogo from "@/components/HerissonLogo";
import { useAuth } from "@/lib/AuthContext";
import { MENU_KEYS, MENU_LABELS, canAccessMenu } from "@/lib/permissions";
import { Menu, LogOut } from "lucide-react";

// Menu items in display order. `superOnly` restricts to super admins.
const ITEMS = [
  { key: MENU_KEYS.ACCUEIL, to: "/admin", end: true },
  { key: MENU_KEYS.RESERVATIONS, to: "/admin/reservations" },
  { key: MENU_KEYS.BILLETTERIE, to: "/admin/billetterie" },
  { key: MENU_KEYS.CONTENU, to: "/admin/content" },
  { key: MENU_KEYS.ADMINS, to: "/admin/users", superOnly: true },
  { key: MENU_KEYS.PERSONNALISATION, to: "/admin/personalization", superOnly: true },
];

export default function AdminLayout({ children }) {
  const { admin, adminLogout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = ITEMS.filter((i) => {
    if (i.superOnly && !admin?.isSuperAdmin) return false;
    return canAccessMenu(admin, i.key);
  });

  const Sidebar = () => (
    <aside className="w-72 shrink-0 bg-[#0e0e10] border-r border-neutral-800 flex flex-col h-full">
      <div className="px-4 pt-6 pb-5 flex flex-col items-center">
        <HerissonLogo size="md" />
        <div className="text-neutral-400 text-xs mt-3 uppercase tracking-widest">Admin console</div>
      </div>
      <div className="h-px bg-neutral-800 mx-6" />
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin">
        {items.map((i) => {
          const meta = MENU_LABELS[i.key];
          return (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${
                  isActive ? "bg-neutral-800 text-white" : "text-neutral-300 hover:bg-neutral-800/60"
                }`
              }
              data-testid={`admin-nav-${i.key}`}
            >
              <span className="text-xl">{meta?.emoji}</span>
              {meta?.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-neutral-800">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-white text-sm">{admin?.name || "Admin"}</div>
          {admin?.isSuperAdmin ? (
            <span className="chip" style={{ background: "#A1C86122", color: "#A1C861", border: "1px solid #A1C86155" }}>ADMIN</span>
          ) : admin?.role && admin.role !== "custom" ? (
            <span className="chip" style={{ background: "#5BC5F222", color: "#5BC5F2", border: "1px solid #5BC5F255" }}>{admin.role.toUpperCase().replace(/_/g, " ")}</span>
          ) : null}
        </div>
        <div className="text-xs text-neutral-400 mt-1 truncate">{admin?.email}</div>
        <button
          onClick={() => { adminLogout(); nav("/"); }}
          className="w-full mt-3 flex items-center justify-center gap-2 bg-neutral-800 hover:bg-red-900/70 hover:text-white text-neutral-200 py-2 px-3 rounded-lg text-sm font-semibold transition-colors"
          data-testid="admin-logout"
        >
          <LogOut size={16} />
          Se déconnecter
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex">
      <div className="hidden lg:block"><Sidebar /></div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0"><Sidebar /></div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-neutral-800">
          <button onClick={() => setOpen(true)} className="p-2"><Menu /></button>
          <HerissonLogo size="xs" />
          <div className="w-8" />
        </header>
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
